import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiPayloadCreator } from "util/api";
import { selectAuthToken } from "redux/features/auth";
import { RESOURCE_TYPE_PARTICIPATION } from "redux/typeConstants";

import { handleExternallyLoadedProfileResource } from "redux/features/profileResources";

const apiCall = createAsyncThunk(
  "paymentProcessing/apiCall",
  apiPayloadCreator("/payments", "post", (success, arg, thunkAPI) => {
    const { personId } = arg;
    if (success.participations)
      success.participations.forEach((participation) => {
        handleExternallyLoadedProfileResource({
          data: participation,
          resourceType: RESOURCE_TYPE_PARTICIPATION,
          resourceId: `${personId}/${participation.activity.id}`,
          personId,
          dispatch: thunkAPI.dispatch,
        });
      });
    return success;
  })
);

const initialState = {
  // We keep a generation number which we increment on every POST,
  // so a stale response to an earlier POST doesn't get misinterpreted
  // as a response to most recent POST. (A PATCH to re-display an old
  // transaction also increments the generation number.)
  generationNumber: 0,

  // Our other redux features typically use a "pending" flag to indicate that
  // an API call is in progress, but because payment transaction records
  // will have a "pending" entry in their payloads we call this "api_pending"
  // instead, to avoid confusion. Also, instead of a pure boolean we record
  // the type of API call in progess: "creating", "completing", or
  // "cancelling", so we set to null when no call is in progress.
  apiPending: null,

  error: null,
  data: null,
};

export const CREATING_PAYMENT = "creating";
export const COMPLETING_PAYMENT = "completing";
export const CANCELLING_PAYMENT = "cancelling";

// All asynchronous action arguments will have either "newGenerationNumber" or
// "transactionId" defined. "newGenerationNumber" is defined when POSTing to
// create a new transaction (in which case transactionId will be undefined) or
// when pulling up a re-display of a previously completed transaction (in
// which case transactionId will be the id of the old transaction to retrieve,
// and unrelated to any previous-generation state data). In this case,
// newGenerationNumber will be one more than the existing state generation
// number. "newGenerationNumber" is undefined when PATCHing to update an
// existing transaction.

// To ensure we don't accidentally process a delayed response to an old API
// call that we've timed out on and moved beyond, the following function
// checks that the arguments of the call the response is for match the
// current state.
function responseIsCurrent(state, arg) {
  const { newGenerationNumber, transactionId, actionType } = arg;

  return (
    actionType === state.apiPending &&
    (newGenerationNumber !== undefined
      ? state.generationNumber === newGenerationNumber
      : state.data && state.data.id === transactionId)
  );
}

export const paymentProcessingSlice = createSlice({
  name: "paymentProcessing",
  initialState: initialState,
  reducers: {
    clear: (state) => {
      state.generationNumber = state.generationNumber + 1;
      state.apiPending = null;
      state.error = null;
      state.data = null;
    },
  },

  extraReducers: {
    [apiCall.pending]: (state, action) => {
      const { arg } = action.meta;
      const { actionType, newGenerationNumber } = arg;
      state.apiPending = actionType || "unknown action";
      state.error = null;
      if (newGenerationNumber !== undefined) {
        // Creating a new transaction (or re-retrieving an old one)
        state.generationNumber = newGenerationNumber;
        state.data = null;
      }
    },

    [apiCall.fulfilled]: (state, action) => {
      const { arg } = action.meta;

      // Discard stale responses
      if (!responseIsCurrent(state, arg)) return;

      state.apiPending = null;
      state.error = null;
      state.data = action.payload;
    },

    [apiCall.rejected]: (state, action) => {
      const { arg } = action.meta;

      // Discard stale responses
      if (!responseIsCurrent(state, arg)) return;

      state.error = { ...action.payload, failedOperation: state.apiPending };
      state.apiPending = null;
    },
  },
});

paymentProcessingSlice.selector = (store) => store.paymentProcessing;

paymentProcessingSlice.asyncActions = {
  begin: (personId, data) => (dispatch, getState) => {
    const state = paymentProcessingSlice.selector(getState());
    dispatch(
      apiCall({
        selectAuthToken,
        data,
        personId,
        path: "/" + personId,
        actionType: CREATING_PAYMENT,
        newGenerationNumber: 1 + state.generationNumber,
      })
    );
  },

  finish: (personId, transactionId, ticket) => (dispatch) =>
    dispatch(
      apiCall({
        selectAuthToken,
        data: { completed: true, ticket },
        personId,
        path: "/" + personId + "/" + transactionId,
        method: "patch",
        actionType: COMPLETING_PAYMENT,
        transactionId,
      })
    ),

  cancel: (personId, transactionId, ticket) => (dispatch) =>
    dispatch(
      apiCall({
        selectAuthToken,
        data: { cancelled: true, ticket },
        personId,
        path: "/" + personId + "/" + transactionId,
        method: "patch",
        actionType: CANCELLING_PAYMENT,
        transactionId,
      })
    ),

  redisplayOldTransaction: (personId, transactionId) => (
    dispatch,
    getState
  ) => {
    const state = paymentProcessingSlice.selector(getState());
    dispatch(
      apiCall({
        selectAuthToken,
        data: { ticket: "n/a", redisplaying: true },
        personId,
        path: "/" + personId + "/" + transactionId,
        method: "patch",
        actionType: COMPLETING_PAYMENT,
        transactionId,
        newGenerationNumber: 1 + state.generationNumber,
      })
    );
  },
};

export default paymentProcessingSlice.reducer;
